import React from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { CheckIcon } from 'lucide-react'


export interface ScanDestination {
  /** Installation row id. Null when the destination doesn't exist yet. */
  id: string | null | undefined
  key: 'subdomain' | 'shopify' | 'manual'
  label: string
  url: string | null
  /** Selectable only when ready — you can't route scans somewhere not installed. */
  ready: boolean
  /** Shown on the row when ready ("Connected"), or as the reason when not. */
  statusLabel: string
  /**
   * How to COLOUR `statusLabel`. Three tones, because the old binary
   * (`ready ? secondary : warning`) conflated two different things and got one of them
   * visibly wrong.
   *
   *   brand    this destination is guaranteed. Only the Vera subdomain: it is created at
   *            signup and hosted by us, so "Always available" is a promise, not a status
   *            to worry about. It was rendering AMBER — the row's `ready` is driven by an
   *            installation record, which a company can lack while still owning the
   *            subdomain, so a row reading "Always available" was painted as a warning.
   *   neutral  a true, unalarming fact. "Not connected" / "Not set up" mean the brand has
   *            not set that destination up — nothing is broken and nothing needs
   *            attention, so amber overstated it. The row's 0.55 opacity already says
   *            "unavailable"; the colour does not need to say it a second time, louder.
   *   warning  something is actually wrong. "Not verified" is the real case: a URL WAS
   *            saved and the check is failing, which is a problem the brand must fix.
   *
   * Defaults to the old behaviour when unset, so a new destination cannot silently lose
   * its warning.
   */
  statusTone?: 'brand' | 'neutral' | 'warning'
}

interface ScanDestinationPickerProps {
  destinations: ScanDestination[]
  selectedKey: ScanDestination['key'] | null
  onSelect: (destination: ScanDestination) => void
  /** Scrolls the setup section for this destination into view. */
  onSetUp: (destination: ScanDestination) => void
  disabled?: boolean
}

/**
 * Picks where a tag scan lands. Selecting a row IS "make primary".
 *
 * Replaces a read-only "Primary location" summary card plus three separate
 * "Make primary" buttons scattered across three cards, plus a paragraph
 * explaining the concept. Primacy is one-of-N, so a picker states it in the
 * control itself and the explanation stops being necessary.
 *
 * Follows the §34 SELECTABLE-card pattern (canonical: FeaturesPage), not the
 * action-row pattern: hover is a brand TINT only, and the brand BORDER is
 * reserved for the chosen row. Using a border for hover here would collide with
 * the selected state and destroy the distinction — the whole point of the
 * control is showing which one is live.
 *
 * A not-ready row is styled as unavailable but stays a real, clickable element.
 * It is deliberately NOT a `disabled` control: disabled elements don't fire click
 * events, so the click-to-setup affordance wouldn't work — you'd have a dead row.
 * (This exact trap already cost us once on this page: an `onClick` on a disabled
 * Input that had never run.) The reason it's unavailable is carried by the status
 * column ("Not verified" / "Not connected"), not a hover card.
 */
export const ScanDestinationPicker = ({
  destinations,
  selectedKey,
  onSelect,
  onSetUp,
  disabled = false,
}: ScanDestinationPickerProps) => {
  const theme = useTheme()

  return (
    <Stack spacing={2} role="radiogroup" aria-label="Scan destination">
      {destinations.map((destination) => {
        const selected = destination.key === selectedKey
        const interactive = destination.ready && !disabled

        const row = (
          <Box
            role="radio"
            aria-checked={selected}
            aria-disabled={!destination.ready || disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (disabled) return
              // Not ready → send them to the setup that would make it ready,
              // rather than doing nothing. The tooltip says the same thing; this
              // is for people who click before reading it.
              if (!destination.ready) return onSetUp(destination)
              if (!selected) onSelect(destination)
            }}
            onKeyDown={(e) => {
              if (disabled) return
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              if (!destination.ready) return onSetUp(destination)
              if (!selected) onSelect(destination)
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              // Every row is the height of a two-line row (label + URL), so a
              // destination with nothing set up yet doesn't sit 19px shorter than
              // its neighbours and read as a different KIND of thing rather than
              // one option of three. Set on the ROW rather than reserving a blank
              // line in the text stack: that kept the heights equal but pinned the
              // lone label to the top, and centring it is what makes the three read
              // as peers. Measured against the two-line rows, which come to 63px.
              minHeight: '64px',
              // 12px between the radio and the label (was 8px), and 16px of left
              // padding (was 12px) so the radio isn't crowded against the row's
              // edge. The radio is small and low-contrast when unselected, so it
              // needs its own breathing room on both sides to read as a control
              // rather than as part of the text block.
              gap: 3,
              p: 3,
              pl: 4,
              borderRadius: 2,
              cursor: disabled ? 'default' : 'pointer',
              // §34: brand BORDER means chosen. Neutral otherwise.
              border: `1px solid ${
                selected ? theme.palette.brand?.[500] || '#7F48DF' : theme.palette.primary[100]
              }`,
              backgroundColor: selected
                ? alpha(theme.palette.brand?.[500] || '#7F48DF', 0.1)
                : 'transparent',
              // Not-ready rows read as unavailable without being `disabled` —
              // they must stay hoverable (tooltip) and clickable (jump to setup).
              opacity: destination.ready ? 1 : 0.55,
              transition: 'background-color .15s ease, border-color .15s ease',
              '&:hover': {
                // §34: TINT for "considering". Never a border — that's selection.
                backgroundColor: selected
                  ? alpha(theme.palette.brand?.[500] || '#7F48DF', 0.1)
                  : interactive
                    ? alpha(theme.palette.brand?.[500] || '#7F48DF', 0.06)
                    : undefined,
              },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.brand?.[600] || '#7F48DF'}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${
                  selected ? theme.palette.brand?.[500] || '#7F48DF' : theme.palette.primary[100]
                }`,
                backgroundColor: selected ? theme.palette.brand?.[500] || '#7F48DF' : 'transparent',
                color: '#fff',
              }}
            >
              {selected && <CheckIcon size={12} strokeWidth={2.5} />}
            </Box>

            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 600 }}>
                {destination.label}
              </Typography>
              {destination.url && (
                <Typography
                  variant="body3"
                  color="text.secondary"
                  sx={{ fontSize: 13, wordBreak: 'break-all' }}
                >
                  {destination.url}
                </Typography>
              )}
            </Stack>

            <Typography
              variant="body3"
              sx={{
                fontSize: 12,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                color:
                  destination.statusTone === 'brand'
                    ? theme.palette.brand?.[500] || '#7F48DF'
                    : destination.statusTone === 'neutral'
                      ? theme.palette.text.secondary
                      : destination.statusTone === 'warning'
                        ? theme.palette.warning.main
                        : // Unset: the original binary, so a destination added later
                          // cannot quietly lose its warning by omission.
                          destination.ready
                          ? theme.palette.text.secondary
                          : theme.palette.warning.main,
              }}
            >
              {destination.statusLabel}
            </Typography>
          </Box>
        )

        // No tooltip. It covered the neighbouring rows, and the row already
        // carries its reason in the status column ("Not verified", "Not
        // connected") — a hover card repeating that in more words was noise on
        // top of a label that already said it. Clicking still jumps to the setup
        // that would make the destination usable.
        return <Box key={destination.key}>{row}</Box>
      })}
    </Stack>
  )
}
